// The filter card that sits above the Inventory resources table.
//
// It renders one labelled row of chips per filter dimension (Category, Asset Type, Risk Overview).
// Every row is multi-select: each chip toggles independently, any number may be lit at once, and no
// selection at all is a valid state. The chips deliberately mirror the ModuleTabs pill styling (see
// ModuleTabs.scss) so the two tab strips on the page read as the same control.

import React from 'react';

import { Box, Skeleton } from '@mui/material';
import { Plus } from 'lucide-react';

import ContentBox from '@root/components/v2/content/ContentBox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@root/components/v3/dropdown-menu';
import Text from '@root/components/v2/ui-elements/Text';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import { formatNumberToTwoDigits } from '@root/hooks/features';
import dataLogo from '@root/assets/svg/Cloud_Events/data-security-logo.svg';
import encryptionLogo from '@root/assets/svg/Cloud_Events/encryption-logo.svg';
import crownJewelLogo from '@root/assets/svg/Cloud_Events/crown-jewel-logo.svg';
import exposedSecretsLogo from '@root/assets/svg/Cloud_Events/exposed-secret.svg';
import highAvailabilityLogo from '@root/assets/svg/Cloud_Events/high-availability.svg';
import identityLogo from '@root/assets/svg/Cloud_Events/identity-logo.svg';
import lateralMovementLogo from '@root/assets/svg/Cloud_Events/lateral-movement.svg';
import loggingMonitoringLogo from '@root/assets/svg/Cloud_Events/logging-and-monitoring.svg';
import Malware from '@root/assets/svg/Cloud_Events/malware.svg';
import misconfigurationIcon from '@root/assets/svg/Cloud_Events/misconfigurationlogo.svg';
import networkLogo from '@root/assets/svg/Cloud_Events/network-logo.svg';
import noRiskLogo from '@root/assets/svg/Cloud_Events/no-risk-logo.svg';
import RuntimeLogo from '@root/assets/svg/Cloud_Events/runtimelogo.svg';
import userLogo from '@root/assets/svg/Cloud_Events/user-logo.svg';
import vulnerabilitiesLogo from '@root/assets/svg/Cloud_Events/vulnerabilities-logo.svg';
import accessKey1NotRotatedLogo from '@root/assets/svg/Risk/RiskPriority/access-key1-not-rotated.svg';
import accessKey2NotRotatedLogo from '@root/assets/svg/Risk/RiskPriority/access-key2-not-rotated.svg';
import administratorLogo from '@root/assets/svg/Risk/RiskPriority/admin.svg';
import anomalousLogo from '@root/assets/svg/Risk/RiskPriority/anomalous.svg';
import attackPathLogo from '@root/assets/svg/Risk/RiskPriority/attack_path.svg';
import credentialExposureLogo from '@root/assets/svg/Risk/RiskPriority/credential-exposure.svg';
import crossAccountLogo from '@root/assets/svg/Risk/RiskPriority/cross-account.svg';
import dataAccessLogo from '@root/assets/svg/Risk/RiskPriority/data-access.svg';
import EffectiveAdmin from '@root/assets/svg/Risk/RiskPriority/effective-admin.png';
import environmentAccessLogo from '@root/assets/svg/Risk/RiskPriority/environment-access.svg';
import inactiveLogo from '@root/assets/svg/Risk/RiskPriority/inactive.png';
import infrastructureModificationLogo from '@root/assets/svg/Risk/RiskPriority/infrastructure-modification.svg';
import learningLogo from '@root/assets/svg/Risk/RiskPriority/learning.svg';
import multipleAccessKeysActiveLogo from '@root/assets/svg/Risk/RiskPriority/multiple-access-keys-active.png';
import noMfaLogo from '@root/assets/svg/Risk/RiskPriority/no-mfa.svg';
import operationsLogo from '@root/assets/svg/Risk/RiskPriority/operations.svg';
import overPrivilegedLogo from '@root/assets/svg/Risk/RiskPriority/over-privileged.svg';
import permissionManagementLogo from '@root/assets/svg/Risk/RiskPriority/permission-management.png';
import privilegeEscalationLogo from '@root/assets/svg/Risk/RiskPriority/privilege-escalation.svg';
import priviledgeLogo from '@root/assets/svg/Risk/RiskPriority/privledge.png';
import reconnaissanceLogo from '@root/assets/svg/Risk/RiskPriority/reconnaissance.svg';
import rootUserLogo from '@root/assets/svg/Risk/RiskPriority/root-user.svg';
import threatLogo from '@root/assets/svg/Risk/RiskPriority/threat.svg';
import environmentIsolationLogo from '@root/assets/svg/Cloud_Events/environment-isolation.svg';
import infrastructureSecurityLogo from '@root/assets/svg/Cloud_Events/infrastructure-security.svg';
import inventoryManagementLogo from '@root/assets/svg/Cloud_Events/inventory-management.svg';
import resourceManagementLogo from '@root/assets/svg/Cloud_Events/resource-management.svg';
import rateLimitingLogo from '@root/assets/svg/Cloud_Events/rate-limiting.svg';
import authenticationLogo from '@root/assets/svg/Cloud_Events/authentication.svg';
import inputValidationLogo from '@root/assets/svg/Cloud_Events/input-validation.svg';
import documentationLogo from '@root/assets/svg/Cloud_Events/documentation.svg';
import attackPreventionLogo from '@root/assets/svg/Cloud_Events/attack-prevention.svg';
import informationDisclosureLogo from '@root/assets/svg/Cloud_Events/information-disclosure.svg';
import complianceLogo from '@root/assets/svg/Cloud_Events/compliance.svg';
import privacyLogo from '@root/assets/svg/Cloud_Events/privacy.svg';
import lifecycleManagementLogo from '@root/assets/svg/Cloud_Events/lifecycle-management.svg';
import riskAssessmentLogo from '@root/assets/svg/Cloud_Events/risk-assessment.svg';
import credentialAccessIcon from '@root/assets/svg/Cloud_Events/credential_access.png';
import workloadSecurityLogo from '@root/assets/svg/Cloud_Events/workload-security-logo.svg';
import thirdPartyLogo from '@root/assets/svg/Cloud_Events/third-party-logo.svg';
import availabilityOperationsLogo from '@root/assets/svg/Cloud_Events/availability-operations.svg';
import identityAccessLogo from '@root/assets/svg/Cloud_Events/identity-access.svg';
import supplyChainLogo from '@root/assets/svg/Cloud_Events/supply-chain.svg';
import vulnerabilityManagementLogo from '@root/assets/svg/Cloud_Events/vulnerability-management.svg';
import workloadHardeningLogo from '@root/assets/svg/Cloud_Events/workload-hardening.svg';
import governanceHygieneLogo from '@root/assets/svg/Cloud_Events/governance-hygiene.svg';

import './ResourceFilterCard.scss';

const CHIP_SKELETON_COUNT = 15;

// eslint-disable-next-line react-refresh/only-export-components
export const active_risk_mapping = {
  ['Data']: dataLogo,
  ['Credential Exposure']: credentialExposureLogo,
  ['Data Exposure']: dataLogo,
  ['Privilege Escalation']: privilegeEscalationLogo,
  ['Reconnaissance']: reconnaissanceLogo,
  ['Privileged']: priviledgeLogo,
  ['Inactive']: inactiveLogo,
  ['Threat']: threatLogo,
  ['Administrator']: administratorLogo,
  ['Admin']: administratorLogo,
  ['Infrastructure Modification']: infrastructureModificationLogo,
  ['Over Privileged']: overPrivilegedLogo,
  ['Data Security']: dataLogo,
  ['Encryption']: encryptionLogo,
  ['Crown Jewel']: crownJewelLogo,
  ['Exposed Secrets']: exposedSecretsLogo,
  ['secrets']: exposedSecretsLogo,
  ['Secrets']: exposedSecretsLogo,
  ['Internet Exposed']: exposedSecretsLogo,
  ['Network']: networkLogo,
  ['Network Security']: networkLogo,
  ['Anomalous']: anomalousLogo,
  ['Logging And Monitoring']: loggingMonitoringLogo,
  ['Logging & Monitoring']: loggingMonitoringLogo,
  ['Lateral Movement']: lateralMovementLogo,
  ['High Availability']: highAvailabilityLogo,
  ['Identity']: identityLogo,
  ['User']: userLogo,
  ['Vulnerabilities']: vulnerabilitiesLogo,
  ['vulnerabilities']: vulnerabilitiesLogo,
  ['NoRisk']: noRiskLogo,
  ['Misconfiguration']: misconfigurationIcon,
  ['Configuration']: misconfigurationIcon,
  ['Malware']: Malware,
  ['Runtime']: RuntimeLogo,
  ['No Mfa']: noMfaLogo,
  ['Cross Account']: crossAccountLogo,
  ['Environment Access']: environmentAccessLogo,
  ['Multiple Access Keys Active']: multipleAccessKeysActiveLogo,
  ['Root User']: rootUserLogo,
  ['Access Key 1 Not Rotated']: accessKey1NotRotatedLogo,
  ['Learning']: learningLogo,
  ['Access Key 2 Not Rotated']: accessKey2NotRotatedLogo,
  ['Attack path']: attackPathLogo,
  ['Operational']: operationsLogo,
  ['Credential Access']: credentialAccessIcon,
  ['Third Party']: thirdPartyLogo,
  ['Availability & Operations']: availabilityOperationsLogo,
  ['Availability And Operations']: availabilityOperationsLogo,
  ['Identity & Access']: identityAccessLogo,
  ['Identity And Access']: identityAccessLogo,
  ['Supply Chain']: supplyChainLogo,
  ['Vulnerability Management']: vulnerabilityManagementLogo,
  ['Workload Hardening']: workloadHardeningLogo,
  ['Governance & Hygiene']: governanceHygieneLogo,
  ['Governance And Hygiene']: governanceHygieneLogo,
  ['Access Control']: credentialAccessIcon,

  ['Effective Admin']: EffectiveAdmin,
  ['Data Access']: dataAccessLogo,
  ['High Privileged']: overPrivilegedLogo,
  ['Misconfigurations']: misconfigurationIcon,
  ['Threats anomaly']: threatLogo,
  ['Anomaly']: anomalousLogo,
  ['Permission Management']: permissionManagementLogo,
  ['Environment Isolation']: environmentIsolationLogo,
  ['Infrastructure Security']: infrastructureSecurityLogo,
  ['Inventory Management']: inventoryManagementLogo,
  ['Resource Management']: resourceManagementLogo,
  ['Rate Limiting']: rateLimitingLogo,
  ['Authentication']: authenticationLogo,
  ['Input Validation']: inputValidationLogo,
  ['Documentation']: documentationLogo,
  ['Attack Prevention']: attackPreventionLogo,
  ['Information Disclosure']: informationDisclosureLogo,
  ['Compliance']: complianceLogo,
  ['Privacy']: privacyLogo,
  ['Lifecycle Management']: lifecycleManagementLogo,
  ['Risk Assessment']: riskAssessmentLogo,
  ['Workload Security']: workloadSecurityLogo,
};

// Icon, name and count for one item — shared by the inline chips and the rows of the "+N" overflow menu.
// `reserveIconSpace` keeps an icon-sized gap for an item with no icon, so menu rows stay aligned.
const ChipContent = ({ name, count, iconKey, icon: Icon, iconColor, iconMap, reserveIconSpace = false }) => {
  const hasCount = count !== undefined && count !== null;
  const short = hasCount ? formatNumberToTwoDigits(count) : '';
  const isAbbreviated = short.endsWith('K') || short.endsWith('M');
  const icon = iconMap?.[iconKey ?? name];

  return (
    <>
      {Icon ? <Icon aria-hidden="true" className="ResourceFilterCard__ChipIcon" style={{ color: iconColor }} /> : null}
      {icon ? <img src={icon} alt="" className="ResourceFilterCard__ChipIcon" /> : null}
      {!Icon && !icon && reserveIconSpace ? <span aria-hidden="true" className="ResourceFilterCard__ChipIcon" /> : null}
      <span>{name}</span>
      {hasCount && (
        <Tooltip title={isAbbreviated ? String(count) : ''} isIcon={true} placement="bottom">
          <span className="ResourceFilterCard__Count">({short})</span>
        </Tooltip>
      )}
    </>
  );
};

/**
 * One labelled chip row. Exported so each filter dimension uses the same layout.
 *
 * @param {string} label row label rendered to the left of the chips
 * @param {Array<{name: string, count?: number, iconKey?: string, icon?: React.ComponentType}>} items one entry per chip
 * @param {string[]} values the currently selected `name`s; `[]` selects nothing, which is a valid state
 * @param {(next: string[]) => void} onChange called with the full next selection after a chip is toggled
 * @param {boolean} loading render chip placeholders instead of chips
 * @param {boolean} disabled dim the row and swallow clicks
 * @param {string} disabledHint tooltip explaining why the row is disabled
 * @param {Record<string, string>} iconMap name -> icon URL
 * @param {number} maxVisible show only the first this-many chips and fold the rest into a "+N" menu; unset shows all
 */
export const FilterChipRow = ({
  label,
  items = [],
  values = [],
  onChange,
  loading = false,
  disabled = false,
  disabledHint = '',
  iconMap,
  showAllChip = false,
  maxVisible,
}) => {
  if (loading && items.length === 0) return null;

  // Tolerates a single name as well as a list, so a caller that still holds one value is not a crash.
  const selected = Array.isArray(values) ? values : values ? [values] : [];

  // Each chip toggles on its own, so several can be lit at once and the last one can be switched off
  // again — the row never forces a selection.
  const toggle = name =>
    onChange(selected.includes(name) ? selected.filter(item => item !== name) : [...selected, name]);

  const isTruncated = maxVisible > 0 && items.length > maxVisible;
  const visibleItems = isTruncated ? items.slice(0, maxVisible) : items;
  const hiddenItems = isTruncated ? items.slice(maxVisible) : [];
  // A selection made from the menu stays in the menu, so the "+N" chip lights up to show it is holding one.
  const hasHiddenSelection = hiddenItems.some(({ name }) => selected.includes(name));

  return (
    <div className="ResourceFilterCard__Row">
      {loading ? (
        <Skeleton variant="rectangular" width="120px" height="25px" sx={{ borderRadius: '5px' }} />
      ) : (
        <Text className="ResourceFilterCard__RowLabel">{`${label} :`}</Text>
      )}

      {loading ? (
        <Box display="flex" flexWrap="wrap" gap="10px">
          {/* A capped row loads as its visible chips plus the "+N" chip, so the card height does not jump. */}
          {Array.from(new Array(maxVisible > 0 ? maxVisible + 1 : CHIP_SKELETON_COUNT)).map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="120px" height="25px" sx={{ borderRadius: '15px' }} />
          ))}
        </Box>
      ) : (
        <Tooltip title={disabled ? disabledHint : ''} isIcon={true}>
          <div
            className="ResourceFilterCard__Chips"
            style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
            {/* Optional "All" chip shown on redirected landings, as a one-click way to clear the whole row. */}
            {showAllChip && (
              <button
                type="button"
                aria-pressed={selected.length === 0}
                className={`ResourceFilterCard__Chip${selected.length === 0 ? ' ResourceFilterCard__Chip--active' : ''}`}
                onClick={() => onChange([])}>
                <span>All</span>
              </button>
            )}
            {visibleItems.map(item => {
              const isActive = selected.includes(item.name);
              const chip = <ChipContent {...item} iconMap={iconMap} />;

              return onChange ? (
                <button
                  key={item.name}
                  type="button"
                  aria-pressed={isActive}
                  className={`ResourceFilterCard__Chip${isActive ? ' ResourceFilterCard__Chip--active' : ''}`}
                  onClick={() => toggle(item.name)}>
                  {chip}
                </button>
              ) : (
                <span key={item.name} className="ResourceFilterCard__Chip" aria-label={item.name}>
                  {chip}
                </span>
              );
            })}

            {hiddenItems.length > 0 && (
              // Non-modal, so opening it neither locks page scroll nor blocks clicks on the rest of the card.
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Show ${hiddenItems.length} more ${label} filters`}
                    className={`ResourceFilterCard__Chip ResourceFilterCard__Chip--more${hasHiddenSelection ? ' ResourceFilterCard__Chip--active' : ''}`}>
                    <Plus aria-hidden="true" className="ResourceFilterCard__ChipIcon" />
                    <span>{hiddenItems.length}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="ResourceFilterCard__Menu">
                  {/* Scrolls inside the menu's rounded frame, so the frame keeps its default overflow-hidden. */}
                  <div className="ResourceFilterCard__MenuList CustomScrollbar">
                    {hiddenItems.map(item => (
                      <DropdownMenuCheckboxItem
                        key={item.name}
                        checked={selected.includes(item.name)}
                        disabled={!onChange}
                        // Keeps the menu open after a pick, so several hidden chips can be toggled in one go
                        // just like the inline ones.
                        onSelect={event => event.preventDefault()}
                        onCheckedChange={() => toggle(item.name)}
                        className="ResourceFilterCard__MenuItem">
                        <ChipContent {...item} iconMap={iconMap} reserveIconSpace={true} />
                      </DropdownMenuCheckboxItem>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

/**
 * Chips only — every action for the resources table (search, region, filter, bulk tag, download) lives
 * in the HeaderBar directly below this card, so the page shows a single row of controls.
 *
 * @param {Array<object>} categoryRows one FilterChipRow config per dimension, in display order
 * @param {Array<object>} assetTypeRows one FilterChipRow config per dimension, in display order
 * @param {Array<object>} riskOverviewRows one FilterChipRow config per dimension, in display order
 *
 */
function ResourceFilterCard({ categoryRows = [], assetTypeRows = [], riskOverviewRows = [] }) {
  return (
    <ContentBox padding="0px" marginBottom="15px" containerClassName="ResourceFilterCard">
      {categoryRows.map(row => (
        <FilterChipRow key={row.key} {...row} />
      ))}
      {assetTypeRows.map(row => (
        <FilterChipRow key={row.key} {...row} />
      ))}
      {riskOverviewRows.map(row => (
        <FilterChipRow key={row.key} {...row} />
      ))}
    </ContentBox>
  );
}

export default ResourceFilterCard;
